import { useEffect, useState } from 'react';

import type { TouchpointSourceType } from '@trello/ad-sequencer';

import { getAccessibleProducts } from '../../getAccessibleProducts';
import { getAvailableSites } from '../../getAvailableSites';
import { useShouldUseAccessibleProducts } from '../../useShouldUseAccessibleProducts';
import type { RuntimeEligibilityCheckResult } from './RuntimeEligibilityCheck';
import { useIsCrossFlowMemberConfirmed } from './useIsCrossFlowMemberConfirmed';

export const useHasNoAvailableSites = ({
  doLoadAvailableSites,
  source,
}: {
  doLoadAvailableSites: boolean;
  source: TouchpointSourceType;
}): RuntimeEligibilityCheckResult => {
  const [loadingAvailableSites, setLoadingAvailableSites] = useState(false);

  const [hasNoAvailableSites, setHasNoAvailableSites] = useState(false);

  const { isEligible: isMemberConfirmed } = useIsCrossFlowMemberConfirmed();

  const shouldUseAccessibleProducts = useShouldUseAccessibleProducts();

  useEffect(() => {
    const loadSiteEligibility = async () => {
      try {
        setLoadingAvailableSites(true);
        if (shouldUseAccessibleProducts) {
          const { products } = await getAccessibleProducts();
          const hasNoSites =
            products?.length > 0 &&
            products?.filter(({ productId }) => productId !== 'trello')
              .length === 0;
          setHasNoAvailableSites(hasNoSites);
        } else {
          const { sites } = await getAvailableSites();
          const hasNoSites =
            sites?.length > 0 &&
            sites?.filter(({ cloudId }) => cloudId !== 'TRELLO').length === 0;
          setHasNoAvailableSites(hasNoSites);
        }
      } finally {
        setLoadingAvailableSites(false);
      }
    };

    // if the user isn't confirmed, they won't have any sites
    if (!isMemberConfirmed) {
      setHasNoAvailableSites(true);
      return;
    }
    if (doLoadAvailableSites && !loadingAvailableSites) {
      loadSiteEligibility();
    }
    // Intentionally ignoring loadingAvailableSites dependency
    // eslint-disable-next-line @trello/exhaustive-deps
  }, [source, doLoadAvailableSites, isMemberConfirmed]);

  return {
    isEligible: hasNoAvailableSites,
  };
};
